import {
  parseISO, isValid, format, differenceInYears,
} from 'date-fns';
import type { TravelerPersonalDetails } from '@marageti/z4-sdk/lib/people';
import type { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';

/**
 * Sentinel year used when the traveler's birth year is unknown.
 * Dates stored with this year display only month and day — no age calculation.
 */
export const YEAR_UNKNOWN_SENTINEL = 1900;

/**
 * Format the nested residence Address into a single display string, omitting blank parts.
 * When a countryCodes map is provided, the 2-letter country code is expanded to its full
 * name (e.g. "US" → "United States"); unknown codes fall back to the raw value.
 * e.g. "123 Main St, Springfield, IL 62701, United States"
 */
export function formatResidence(
  details: TravelerPersonalDetails | undefined,
  countryCodes?: CountryCodeMap,
): string | undefined {
  if (!details?.residence) return undefined;
  const {
    street1, street2, city, state, zipcode, country,
  } = details.residence;
  const cityStateZip = [city, [state, zipcode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const countryName = country ? (countryCodes?.[country] ?? country) : '';
  const parts = [street1, street2, cityStateZip, countryName].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/**
 * Parse an ISO / RFC3339 birthday string into a Date object.
 * Returns undefined for empty, invalid, or Go zero-time values
 * ("0001-01-01T00:00:00Z" is how Go serializes a zero time.Time).
 *
 * Returns a *local* date whose calendar date matches the UTC date in the string,
 * so callers can use getFullYear/getMonth/getDate without timezone shifting.
 */
export function birthdayStringToDate(value: string | undefined): Date | undefined {
  if (!value?.trim()) return undefined;
  const date = parseISO(value);
  if (!isValid(date)) return undefined;
  if (date.getUTCFullYear() <= 1) return undefined;
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Format a birthday string for display:
 * - Known year  → "Apr 15, 1981 (44 years old)"
 * - Unknown year (sentinel 1900) → "Apr 15"
 * Returns undefined for empty or invalid input.
 */
export function formatBirthdayWithAge(birthday: string | undefined): string | undefined {
  if (!birthday?.trim()) return undefined;
  const date = parseISO(birthday);
  if (!isValid(date)) return birthday;
  if (date.getUTCFullYear() <= 1) return undefined;
  const localDate = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  if (date.getUTCFullYear() === YEAR_UNKNOWN_SENTINEL) {
    return format(localDate, 'MMM d');
  }
  const age = differenceInYears(new Date(), localDate);
  return `${format(localDate, 'MMM d, yyyy')} (${age} years old)`;
}
